// Big center-screen dollar amount. Ticks linearly from face value to 10% floor over the
// 10-second hard window per GDD §5.2, clamped at floor during grace. Syncs to
// server-authoritative `reveal_at_ms` via clock-sync so the drop matches the streamer's
// visible clue.

import { Mesh, MeshBasicMaterial, PlaneGeometry, type Texture } from 'three';
import { textTexture } from './lib/text-texture.js';
import { FONT_DISPLAY } from './lib/theme.js';

const HARD_WINDOW_MS = 10_000;
const TICKER_W = 520;
const TICKER_H = 170;

export interface DollarTickerHandle {
  mesh: Mesh;
  /** Called on clue_reveal. `startLocalMs` is performance.now() equivalent of reveal_at_ms. */
  arm: (faceValue: number, startLocalMs: number) => void;
  /** Flip to flat floor display (grace window). */
  lockToFloor: () => void;
  /** Hide; used when leaving the clue scene. */
  reset: () => void;
  tick: (nowMs: number) => void;
  dispose: () => void;
}

interface ArmedState {
  faceValue: number;
  floor: number;
  startLocalMs: number;
  locked: boolean;
}

export function createDollarTicker(): DollarTickerHandle {
  const geom = new PlaneGeometry(TICKER_W, TICKER_H);
  const mat = new MeshBasicMaterial({ transparent: true, depthWrite: false });
  const mesh = new Mesh(geom, mat);
  mesh.renderOrder = 60;
  mesh.visible = false;

  let armed: ArmedState | null = null;
  let lastDrawn = -1;

  function draw(value: number): void {
    if (value === lastDrawn) return;
    lastDrawn = value;
    const tex: Texture = textTexture(`${value}`, {
      font: `160px ${FONT_DISPLAY}`,
      color: '#FFC43D',
      width: TICKER_W,
      height: TICKER_H,
      weight: 'bold',
      stroke: '#000000',
      strokeWidth: 6,
    });
    if (mat.map && mat.map !== tex) mat.map.dispose();
    mat.map = tex;
    mat.needsUpdate = true;
  }

  function arm(faceValue: number, startLocalMs: number): void {
    armed = {
      faceValue,
      floor: Math.round(0.1 * faceValue),
      startLocalMs,
      locked: false,
    };
    mesh.visible = true;
    draw(faceValue);
  }

  function lockToFloor(): void {
    if (!armed) return;
    armed.locked = true;
    draw(armed.floor);
  }

  function reset(): void {
    armed = null;
    mesh.visible = false;
    lastDrawn = -1;
  }

  function tick(nowMs: number): void {
    if (!armed) return;
    if (armed.locked) {
      draw(armed.floor);
      return;
    }
    const dt = nowMs - armed.startLocalMs;
    if (dt <= 0) {
      draw(armed.faceValue);
      return;
    }
    const t = Math.min(1, dt / HARD_WINDOW_MS);
    const raw = Math.round(armed.faceValue * (1 - t));
    const display = Math.max(armed.floor, raw);
    draw(display);
  }

  return {
    mesh,
    arm,
    lockToFloor,
    reset,
    tick,
    dispose() {
      geom.dispose();
      mat.dispose();
    },
  };
}
